"use client";

import { memo, useCallback, useEffect, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ColumnsIcon,
  MinusIcon,
  PlusIcon,
  RectangleHorizontalIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePdfViewerContext } from "./PdfViewerCore";
import { usePdfDocumentStore } from "./pdfDocumentStore";
import { usePdfViewMode } from "./pdfViewModeContext";
import { applyDiffZoom } from "./PdfDiffView";
import { setPdfZoom } from "@/lib/storage";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";

// A4 ratio duplicated here so the toolbar can recompute on demand without
// importing from PdfDiffView (avoids a potential module-init cycle).
const A4_RATIO = 297 / 210;

function computeDiffFitPage(iframe: HTMLIFrameElement): number {
  const container = iframe.parentElement;
  if (!container) return 1;
  const naturalWidth = iframe.contentDocument?.documentElement.scrollWidth ?? 0;
  if (naturalWidth === 0) return 1;
  return Math.min(
    container.clientWidth / naturalWidth,
    container.clientHeight / (naturalWidth * A4_RATIO),
  );
}

function computeDiffFitWidth(iframe: HTMLIFrameElement): number {
  const container = iframe.parentElement;
  if (!container) return 1;
  const naturalWidth = iframe.contentDocument?.documentElement.scrollWidth ?? 0;
  if (naturalWidth === 0) return 1;
  return container.clientWidth / naturalWidth;
}

/**
 * Bottom toolbar — page navigation + zoom. Page navigation is hidden in diff
 * mode (diff is a single scrollable document, not paginated). Zoom buttons
 * work for both the PDF viewer (via pdfjs currentScaleValue) and the HTML diff
 * (via contentDocument.documentElement.style.zoom). User's zoom preference is
 * persisted to localStorage so it survives page reloads.
 */
export const PdfToolbar = memo(function PdfToolbar() {
  const { t } = useTranslation();
  const { instanceRef, isReady } = usePdfViewerContext();
  const { viewMode, diffIframeRef, diffZoom, setDiffZoom } = usePdfViewMode();
  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);
  const documents = usePdfDocumentStore((s) => s.documents);
  const activeDoc = documents.find((d) => d.id === activeDocumentId);

  const isDiff = viewMode === "diff";

  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!isReady) return;
    const eb = instanceRef.current?.eventBus;
    if (!eb) return;

    const onPageChange = (e: { pageNumber: number }) =>
      setCurrentPage(e.pageNumber);
    const onScaleChange = (e: { scale: number }) =>
      setScale(e.scale);

    eb.on("pagechanging", onPageChange);
    eb.on("scalechanging", onScaleChange);
    return () => {
      eb.off("pagechanging", onPageChange);
      eb.off("scalechanging", onScaleChange);
    };
  }, [isReady, instanceRef]);

  useEffect(() => {
    setCurrentPage(1);
    setScale(1);
  }, [activeDocumentId]);

  const goToPage = useCallback(
    (page: number) => {
      const viewer = instanceRef.current?.viewer;
      if (!viewer || !activeDoc) return;
      const clamped = Math.max(1, Math.min(page, activeDoc.pageCount ?? 1));
      viewer.currentPageNumber = clamped;
    },
    [instanceRef, activeDoc],
  );

  const changeScale = useCallback(
    (delta: number) => {
      if (isDiff) {
        const iframe = diffIframeRef.current;
        if (!iframe?.contentDocument) return;
        const newZoom = Math.max(0.25, Math.min(5, diffZoom + delta));
        applyDiffZoom(iframe, newZoom);
        setDiffZoom(newZoom);
        setPdfZoom(String(newZoom));
      } else {
        const viewer = instanceRef.current?.viewer;
        if (!viewer) return;
        const newScale = Math.max(0.25, Math.min(5, scale + delta));
        viewer.currentScaleValue = String(newScale);
        setPdfZoom(String(newScale));
      }
    },
    [isDiff, diffIframeRef, diffZoom, setDiffZoom, instanceRef, scale],
  );

  const fitPage = useCallback(() => {
    if (isDiff) {
      const iframe = diffIframeRef.current;
      if (!iframe?.contentDocument) return;
      // Reset first so scrollWidth reflects natural (unscaled) size.
      iframe.contentDocument.documentElement.style.zoom = "1";
      const zoom = computeDiffFitPage(iframe);
      applyDiffZoom(iframe, zoom);
      setDiffZoom(zoom);
      setPdfZoom("page-fit");
    } else {
      const viewer = instanceRef.current?.viewer;
      if (viewer) {
        viewer.currentScaleValue = "page-fit";
        setPdfZoom("page-fit");
      }
    }
  }, [isDiff, diffIframeRef, setDiffZoom, instanceRef]);

  const fitWidth = useCallback(() => {
    if (isDiff) {
      const iframe = diffIframeRef.current;
      if (!iframe?.contentDocument) return;
      iframe.contentDocument.documentElement.style.zoom = "1";
      const zoom = computeDiffFitWidth(iframe);
      applyDiffZoom(iframe, zoom);
      setDiffZoom(zoom);
      setPdfZoom("page-width");
    } else {
      const viewer = instanceRef.current?.viewer;
      if (viewer) {
        viewer.currentScaleValue = "page-width";
        setPdfZoom("page-width");
      }
    }
  }, [isDiff, diffIframeRef, setDiffZoom, instanceRef]);

  if (!activeDoc) return null;

  const displayScale = isDiff ? diffZoom : scale;

  return (
    <div className="flex h-10 items-center justify-center gap-1 border-t border-border/40 bg-background/80 backdrop-blur-sm px-3 text-xs text-muted-foreground">
      {/* Page navigation — hidden in diff mode (diff is a single scrollable document) */}
      {!isDiff && (
        <>
          <div className="flex items-center gap-0.5">
            <TooltipIconButton
              tooltip={t("pdf.previousPage")}
              className="size-7 rounded-md cursor-pointer hover:bg-muted"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <ChevronLeftIcon className="size-3.5" />
            </TooltipIconButton>
            <span className="tabular-nums px-1">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => goToPage(Number(e.target.value))}
                className="w-10 text-center bg-muted/50 border border-border/40 rounded-md py-0.5 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="text-muted-foreground/60 ml-1">/ {activeDoc.pageCount ?? 1}</span>
            </span>
            <TooltipIconButton
              tooltip={t("pdf.nextPage")}
              className="size-7 rounded-md cursor-pointer hover:bg-muted"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= (activeDoc.pageCount ?? 1)}
            >
              <ChevronRightIcon className="size-3.5" />
            </TooltipIconButton>
          </div>

          <span className="mx-2 h-4 w-px bg-border/40" />
        </>
      )}

      {/* Zoom controls */}
      <div className="flex items-center gap-0.5">
        <TooltipIconButton
          tooltip={t("pdf.zoomOut")}
          className="size-7 rounded-md cursor-pointer hover:bg-muted"
          onClick={() => changeScale(-0.10)}
        >
          <MinusIcon className="size-3.5" />
        </TooltipIconButton>
        <span className="tabular-nums w-10 text-center text-xs">
          {Math.round(displayScale * 100)}%
        </span>
        <TooltipIconButton
          tooltip={t("pdf.zoomIn")}
          className="size-7 rounded-md cursor-pointer hover:bg-muted"
          onClick={() => changeScale(0.10)}
        >
          <PlusIcon className="size-3.5" />
        </TooltipIconButton>

        <span className="mx-1.5 h-4 w-px bg-border/40" />

        <TooltipIconButton
          tooltip={t("pdf.fitPage")}
          className="size-7 rounded-md cursor-pointer hover:bg-muted"
          onClick={fitPage}
        >
          <RectangleHorizontalIcon className="size-3.5" />
        </TooltipIconButton>
        <TooltipIconButton
          tooltip={t("pdf.fitWidth")}
          className="size-7 rounded-md cursor-pointer hover:bg-muted"
          onClick={fitWidth}
        >
          <ColumnsIcon className="size-3.5" />
        </TooltipIconButton>
      </div>
    </div>
  );
});
